import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CollectionDetail from "../components/photography/CollectionDetail";
import PhotoLightbox from "../components/photography/PhotoLightbox";
import PhotographyLanding from "../components/photography/PhotographyLanding";
import PageMeta from "../components/seo/PageMeta";
import { getCollectionById } from "../data/photography";
import { useShowcasePhotos } from "../hooks/useShowcasePhotos";
import {
  getShowcaseIndexByPhotoId,
  isCanonicalShowcasePath,
  photographyShowcaseHref,
  showcaseOgImageUrl,
} from "../lib/homeHeroPhotos";
import {
  absoluteUrl,
  buildBreadcrumbJsonLd,
  buildImageGalleryJsonLd,
  buildWebPageJsonLd,
} from "../lib/seo";
import "../styles/photography.css";

function CollectionDetailComingSoonOverlay() {
  return (
    <div className="photo-collection-detail-coming-soon__overlay" role="status" aria-live="polite">
      <p className="photo-collection-detail-coming-soon__heading">COMING SOON</p>
      <p className="photo-collection-detail-coming-soon__note">
        Sorry, not enough time during the day but I am working on this.
      </p>
    </div>
  );
}

function CollectionDetailComingSoonShell({ children }) {
  return (
    <div className="photo-collection-detail-coming-soon-shell">
      {children}
      <CollectionDetailComingSoonOverlay />
    </div>
  );
}

export default function PhotographyPage() {
  const { collectionId, photoId: routePhotoId, photoSlug: routePhotoSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const collection = collectionId ? getCollectionById(collectionId) : null;
  const legacyShowcaseParam = searchParams.get("showcase");
  const showcaseParam = routePhotoId || legacyShowcaseParam;
  const { photos: showcasePhotos, loading: showcaseLoading } = useShowcasePhotos();
  const [activeTab, setActiveTab] = useState("showcase");
  const [lightbox, setLightbox] = useState(null);

  const syncShowcasePath = useCallback(
    (photo) => {
      if (collectionId || !photo?.id) {
        return;
      }
      navigate(photographyShowcaseHref(photo), { replace: true });
    },
    [collectionId, navigate]
  );

  const clearShowcasePath = useCallback(() => {
    if (routePhotoId || legacyShowcaseParam) {
      navigate("/photography", { replace: true });
    }
  }, [legacyShowcaseParam, navigate, routePhotoId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [collectionId]);

  useEffect(() => {
    if (collectionId && !collection) {
      navigate("/photography", { replace: true });
    }
  }, [collectionId, collection, navigate]);

  useEffect(() => {
    if (collectionId || !showcaseParam || showcaseLoading) {
      return;
    }

    const index = getShowcaseIndexByPhotoId(showcasePhotos, showcaseParam);
    if (index < 0) {
      if (routePhotoId || legacyShowcaseParam) {
        navigate("/photography", { replace: true });
      }
      return;
    }

    const photo = showcasePhotos[index];
    const canonicalPath = photographyShowcaseHref(photo);

    if (legacyShowcaseParam && !routePhotoId) {
      navigate(canonicalPath, { replace: true });
      return;
    }

    if (routePhotoId && !isCanonicalShowcasePath(photo, routePhotoSlug)) {
      navigate(canonicalPath, { replace: true });
      return;
    }

    setLightbox((current) => {
      if (current?.list === showcasePhotos && current.index === index) {
        return current;
      }
      return { list: showcasePhotos, index };
    });
  }, [
    collectionId,
    legacyShowcaseParam,
    navigate,
    routePhotoId,
    routePhotoSlug,
    showcaseLoading,
    showcaseParam,
    showcasePhotos,
  ]);

  const openLightbox = useCallback(
    (list, index) => {
      setLightbox({ list, index });
      const photo = list[index];
      if (photo?.id) {
        syncShowcasePath(photo);
      }
    },
    [syncShowcasePath]
  );

  const closeLightbox = useCallback(() => {
    setLightbox(null);
    clearShowcasePath();
  }, [clearShowcasePath]);

  const changeLightboxIndex = useCallback(
    (index) => {
      setLightbox((current) => {
        if (!current) {
          return current;
        }
        const photo = current.list[index];
        if (photo?.id) {
          syncShowcasePath(photo);
        }
        return { ...current, index };
      });
    },
    [syncShowcasePath]
  );

  const openCollection = useCallback(
    (id) => {
      navigate(`/photography/collections/${id}`);
    },
    [navigate]
  );

  const backToCollections = useCallback(() => {
    navigate("/photography");
    setActiveTab("collections");
    window.setTimeout(() => {
      const section = document.getElementById("sub-collections");
      if (section) {
        const top = window.scrollY + section.getBoundingClientRect().top - 78;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }
    }, 50);
  }, [navigate]);

  const collectionCoverKey =
    collection?.cover?.src || collection?.cover?.imageKey || collection?.photos?.[0]?.src;
  const collectionPath = collection ? `/photography/collections/${collection.id}` : "/photography";
  const lightboxShareUrl = useMemo(() => {
    if (!lightbox || collectionId) {
      return null;
    }
    const photo = lightbox.list[lightbox.index];
    if (!photo?.id) {
      return null;
    }
    return absoluteUrl(photographyShowcaseHref(photo));
  }, [collectionId, lightbox]);

  const showcasePhoto = lightbox && !collectionId ? lightbox.list[lightbox.index] : null;
  const sharedShowcasePhoto = useMemo(() => {
    if (collectionId || !showcaseParam || showcaseLoading) {
      return null;
    }
    const index = getShowcaseIndexByPhotoId(showcasePhotos, showcaseParam);
    if (index < 0) {
      return null;
    }
    return showcasePhotos[index];
  }, [collectionId, showcaseLoading, showcaseParam, showcasePhotos]);

  const activeShowcasePhoto = showcasePhoto || sharedShowcasePhoto;
  const metaTitle = activeShowcasePhoto
    ? activeShowcasePhoto.t
    : collection
      ? `${collection.title} — Photography Collection`
      : "Photography";
  const metaDescription = activeShowcasePhoto?.desc
    ? activeShowcasePhoto.desc
    : collection
      ? `${collection.subtitle} Browse ${collection.count} photographs by CJ Jayamaha.`
      : "Fine art, documentary, wildlife, travel, and motorsport photography by CJ Jayamaha — browse signature frames, collections, and prints.";
  const metaPath = activeShowcasePhoto?.id
    ? photographyShowcaseHref(activeShowcasePhoto)
    : collectionPath;
  const metaImage = activeShowcasePhoto?.id
    ? showcaseOgImageUrl(activeShowcasePhoto.id)
    : collectionCoverKey;
  const metaTwitterImage = activeShowcasePhoto?.id
    ? showcaseOgImageUrl(activeShowcasePhoto.id, "twitter")
    : undefined;
  const metaImageAlt = activeShowcasePhoto
    ? `${activeShowcasePhoto.t} — photography by CJ Jayamaha`
    : undefined;

  return (
    <>
      <PageMeta
        title={metaTitle}
        description={metaDescription}
        path={metaPath}
        image={metaImage}
        imageAlt={metaImageAlt}
        imageWidth={activeShowcasePhoto ? 1200 : undefined}
        imageHeight={activeShowcasePhoto ? 630 : undefined}
        imageType={activeShowcasePhoto ? "image/jpeg" : undefined}
        twitterImage={metaTwitterImage}
        twitterImageWidth={activeShowcasePhoto ? 1200 : undefined}
        twitterImageHeight={activeShowcasePhoto ? 600 : undefined}
        keywords={
          activeShowcasePhoto
            ? [...(activeShowcasePhoto.kw || []), "photography", "CJ Jayamaha"]
            : collection
              ? [collection.title, "photography collection", "CJ Jayamaha", collection.subtitle]
              : ["photography", "prints", "wildlife", "travel", "motorsport", "CJ Jayamaha"]
        }
        jsonLd={
          activeShowcasePhoto
            ? buildWebPageJsonLd({
                title: metaTitle,
                description: metaDescription,
                path: metaPath,
                image: metaImage,
              })
            : collection
              ? [
                  buildWebPageJsonLd({
                    title: `${collection.title} — Photography Collection`,
                    description: collection.subtitle,
                    path: collectionPath,
                    image: collectionCoverKey,
                  }),
                  buildImageGalleryJsonLd({
                    title: collection.title,
                    description: collection.subtitle,
                    path: collectionPath,
                    image: collectionCoverKey,
                    collectionSize: collection.count,
                  }),
                  buildBreadcrumbJsonLd([
                    { name: "Home", path: "/" },
                    { name: "Photography", path: "/photography" },
                    { name: collection.title, path: collectionPath },
                  ]),
                ]
              : buildWebPageJsonLd({
                  title: "Photography by CJ Jayamaha",
                  description:
                    "Fine art, documentary, wildlife, travel, and motorsport photography by CJ Jayamaha.",
                  path: "/photography",
                })
        }
      />
      {collection ? (
        <CollectionDetailComingSoonShell>
          <CollectionDetail
            collection={collection}
            onBack={backToCollections}
            onOpenLightbox={openLightbox}
          />
        </CollectionDetailComingSoonShell>
      ) : (
        <PhotographyLanding
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onOpenLightbox={openLightbox}
          onOpenCollection={openCollection}
          initialShowcaseId={showcaseParam}
          showcasePhotos={showcasePhotos}
          showcaseLoading={showcaseLoading}
        />
      )}

      <PhotoLightbox
        lightbox={lightbox}
        onClose={closeLightbox}
        onChangeIndex={changeLightboxIndex}
        shareUrl={lightboxShareUrl}
      />
    </>
  );
}
